import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { IWeissmanModalComponent } from '../../../Compliance/WeissmanModalService';
import { ActionViewCategoryTypes, ActionViewDisplayTypes } from '../../../constants.new';
import { ActionViewCategoryInput } from '../../../Core-Repositories/actionView.repository';
import { toActionViewSavedCriteriaPayload } from '../actionView.adapters';
import { ActionViewApiService } from '../actionView.service';
import {
    ActionViewDefinition,
    ActionViewSavedCriteria,
    ActionViewSavedSearch,
    TaskActionViewThatAreOption,
} from '../actionView.models';

export interface ActionViewSaveParams {
    definition: ActionViewDefinition;
    criteria: ActionViewSavedCriteria;
    hasEditPermission: boolean;
}

export interface ActionViewSaveResult {
    actionView: ActionViewSavedSearch;
    actionViewType: ActionViewCategoryTypes;
}

@Component({
    selector: 'action-view-save',
    templateUrl: './actionViewSave.component.html',
    standalone: false,
})
export class ActionViewSaveComponent
    implements OnInit, IWeissmanModalComponent<ActionViewSaveParams, ActionViewSaveResult | undefined>
{
    constructor(
        private readonly modalRef: BsModalRef,
        private readonly actionViewService: ActionViewApiService,
        private readonly toastr: ToastrService,
    ) {}

    params: ActionViewSaveParams;
    result: ActionViewSaveResult | undefined;

    readonly categoryTypes = ActionViewCategoryTypes;
    readonly displayTypes = ActionViewDisplayTypes;

    categories: Core.ActionViewSavedSearchCategoryManagementDTO[] = [];
    actionViewType: ActionViewCategoryTypes = ActionViewCategoryTypes.CUSTOM;
    selectedDisplayType: ActionViewDisplayTypes = ActionViewDisplayTypes.FILTER;
    selectedCategoryId: number | 'new' | null = null;
    actionViewName: string = '';
    newCategoryName: string = '';
    saving: boolean = false;

    get completed(): boolean {
        return this.params.criteria.masterFilter.thatAreChoice === TaskActionViewThatAreOption.Completed;
    }

    get filteredCategories(): Core.ActionViewSavedSearchCategoryManagementDTO[] {
        return this.categories.filter((category) => Number(category.categoryType) === Number(this.actionViewType));
    }

    async ngOnInit(): Promise<void> {
        this.actionViewType = (this.params.definition.actionViewType ??
            ActionViewCategoryTypes.CUSTOM) as ActionViewCategoryTypes;
        this.selectedDisplayType = this.completed
            ? ActionViewDisplayTypes.FILTER
            : (this.params.definition.defaultDisplay as ActionViewDisplayTypes);
        this.selectedCategoryId = this.params.definition.categoryId;
        this.actionViewName = this.params.definition.actionViewName;
        this.categories = (await this.actionViewService.getCategories()).sort((left, right) =>
            left.categoryName.localeCompare(right.categoryName),
        );
        this.ensureCategorySelection();
    }

    typeChanged(type: ActionViewCategoryTypes): void {
        this.actionViewType = Number(type) as ActionViewCategoryTypes;
        this.selectedCategoryId = null;
        this.ensureCategorySelection();
    }

    createNewCategory(): void {
        const name = this.newCategoryName.trim();
        if (!name) {
            return;
        }
        this.selectedCategoryId = 'new';
    }

    async save(): Promise<void> {
        if (!this.actionViewName.trim() || this.saving) {
            return;
        }
        this.saving = true;
        try {
            let categoryId = this.selectedCategoryId;
            if (categoryId === 'new') {
                const category: ActionViewCategoryInput = {
                    categoryName: this.newCategoryName.trim(),
                    categoryType: this.actionViewType as unknown as Core.SearchCategoryType,
                };
                categoryId = (await this.actionViewService.createCategory(category)).categoryId;
            }
            if (this.actionViewType === ActionViewCategoryTypes.SYSTEM && categoryId === null) {
                this.toastr.error('System Action Views must be saved in a category.', 'Error');
                return;
            }
            const actionView: ActionViewSavedSearch = {
                actionViewName: this.actionViewName.trim(),
                actionViewId: 0,
                criteria: toActionViewSavedCriteriaPayload(this.params.criteria),
                categoryId,
                categoryName:
                    this.categories.find((category) => category.categoryId === categoryId)?.categoryName ?? null,
                isRyanInternal: false,
                isFavorite: false,
                defaultDisplay: this.completed ? ActionViewDisplayTypes.FILTER : this.selectedDisplayType,
                sequence:
                    this.categories.find((category) => category.categoryId === categoryId)?.actionViewList.length ?? 0,
                isShared: false,
                isOwnedByUser: true,
                userID: null,
                rowVersion: [],
                isSystemSearch: this.actionViewType === ActionViewCategoryTypes.SYSTEM,
            };
            const saved = await this.actionViewService.createActionView(actionView, this.actionViewType);
            this.result = { actionView: saved, actionViewType: this.actionViewType };
            this.modalRef.hide();
        } catch (error) {
            const status = typeof error === 'object' && error !== null && 'status' in error ? error.status : null;
            this.toastr.error(status === 409 ? 'Action View name/category already exists' : 'Server Error', 'Error');
        } finally {
            this.saving = false;
        }
    }

    cancel(): void {
        this.result = undefined;
        this.modalRef.hide();
    }

    private ensureCategorySelection(): void {
        if (this.filteredCategories.some((category) => category.categoryId === this.selectedCategoryId)) {
            return;
        }
        this.selectedCategoryId = this.filteredCategories[0]?.categoryId ?? null;
    }
}
